import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { doc, updateDoc, serverTimestamp, collection, onSnapshot, query, where, orderBy, addDoc } from 'firebase/firestore';
import PatrolModal from './PatrolModal';
import { can } from './roles';
import { downloadEvidence } from './evidenceExport';

const S = {
  page: { padding: '0 24px 24px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 16px' },
  title: { fontSize: 20, fontWeight: 900, color: '#1A1A2E' },
  sub: { fontSize: 12, color: '#9B8F7A', marginTop: 2 },
  btn: (color) => ({ background: color || '#1A1A2E', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }),
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 },
  card: { background: '#FFFFFF', border: '1px solid #E2DDD6', borderRadius: 12, padding: 20 },
  badge: (color) => ({ background: color + '22', color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block' }),
  tabBtn: (active) => ({ background: active ? '#1A1A2E' : '#FFFFFF', color: active ? '#C9A84C' : '#6B6055', border: '1px solid #E2DDD6', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 400 }),
  lbl: { fontSize: 10, color: '#9B8F7A', letterSpacing: 1.5, display: 'block', marginBottom: 5, textTransform: 'uppercase', fontWeight: 700 },
  inp: { background: '#FAFAF8', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '9px 12px', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  sel: { background: '#FAFAF8', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '9px 12px', borderRadius: 7, fontSize: 13, width: '100%' },
};

const fmt = (ts) => { try { const d = ts?.toDate?.() || new Date(ts); return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

// A patrol-issue photo with a gated Download (via the exportEvidence function —
// role-gated + audited server-side). Officers don't see the button; the function
// enforces it regardless.
function IssueEvidence({ issue, canExport }) {
  const [downloading, setDownloading] = useState(false);
  const [dlErr, setDlErr] = useState('');
  if (!issue.photo_url) return null;
  const doDownload = async () => {
    setDownloading(true); setDlErr('');
    try { await downloadEvidence({ type: 'patrol_issue', id: issue.id, url: issue.photo_url }); }
    catch (e) { setDlErr(e.message || 'Download failed'); }
    finally { setDownloading(false); }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <a href={issue.photo_url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
        <img src={issue.photo_url} alt="Issue" referrerPolicy="no-referrer"
          style={{ maxWidth: 300, borderRadius: 8, border: '1px solid #E2DDD6', display: 'block' }} />
      </a>
      {canExport && (
        <button onClick={doDownload} disabled={downloading}
          style={{ ...S.btn('#1E3A5F'), marginTop: 6, padding: '5px 12px', fontSize: 11, opacity: downloading ? 0.6 : 1 }}>
          {downloading ? 'Preparing…' : '⬇ Download'}
        </button>
      )}
      {dlErr && <div style={{ color: '#C0392B', fontSize: 11, marginTop: 4 }}>{dlErr}</div>}
    </div>
  );
}

// A patrol checkpoint photo with a gated Download (via the exportEvidence
// function — role-gated + audited server-side, type 'patrol'). Officers don't
// see the button; the function enforces it regardless.
function CheckpointPhoto({ url, patrolId, canExport }) {
  const [downloading, setDownloading] = useState(false);
  const [dlErr, setDlErr] = useState('');
  if (!url) return null;
  const doDownload = async () => {
    setDownloading(true); setDlErr('');
    try { await downloadEvidence({ type: 'patrol', id: patrolId, url }); }
    catch (e) { setDlErr(e.message || 'Download failed'); }
    finally { setDownloading(false); }
  };
  return (
    <div style={{ marginTop: 6 }}>
      <a href={url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
        <img src={url} alt="Checkpoint" referrerPolicy="no-referrer"
          style={{ maxWidth: 220, borderRadius: 8, border: '1px solid #E2DDD6', display: 'block' }} />
      </a>
      {canExport && (
        <button onClick={doDownload} disabled={downloading}
          style={{ ...S.btn('#1E3A5F'), marginTop: 6, padding: '5px 12px', fontSize: 11, opacity: downloading ? 0.6 : 1 }}>
          {downloading ? 'Preparing…' : '⬇ Download'}
        </button>
      )}
      {dlErr && <div style={{ color: '#C0392B', fontSize: 11, marginTop: 4 }}>{dlErr}</div>}
    </div>
  );
}

export default function PatrolsPage({ patrols, routes, tenantId, userRole, focusLog, onFocusHandled }) {
  const canExport = can(userRole, 'exportEvidence');
  const [tab,         setTab]         = useState('routes');
  const [showModal,   setShowModal]   = useState(false);
  const [editingRoute,setEditingRoute]= useState(null);
  const [officers,    setOfficers]    = useState([]);
  const [issues,      setIssues]      = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);

  // Deep-link from global search: jump to the Patrol Logs tab and open the log.
  useEffect(() => {
    if (!focusLog) return;
    setTab('logs');
    setSelectedLog(focusLog);
    onFocusHandled && onFocusHandled();
  }, [focusLog]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = query(collection(db, 'users'), where('tenant_id', '==', tenantId));
    return onSnapshot(q, s => setOfficers(s.docs.map(d => ({ uid: d.id, ...d.data() }))));
  }, [tenantId]);

  useEffect(() => {
    const q = query(collection(db, 'patrol_issues'), where('tenant_id', '==', tenantId), orderBy('created_at', 'desc'));
    return onSnapshot(q, s => setIssues(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [tenantId]);

  const assignOfficer = async (routeId, officerUid, officerName) => {
    await updateDoc(doc(db, 'patrol_routes', routeId), { assigned_to: officerUid, assigned_name: officerName, updated_at: serverTimestamp() });
  };

  const resolveIssue = async (id) => {
    await updateDoc(doc(db, 'patrol_issues', id), { status: 'resolved', resolved_at: serverTimestamp(), resolved_by: auth.currentUser?.email });
  };

  const stats = {
    routes:    routes.length,
    completed: patrols.filter(p => p.completed).length,
    incomplete:patrols.filter(p => !p.completed).length,
    issues:    issues.filter(i => i.status !== 'resolved').length,
  };

  return (
    <div style={S.page}>
      {showModal && (
        <PatrolModal route={editingRoute} tenantId={tenantId}
          onClose={() => { setShowModal(false); setEditingRoute(null); }}
          onSaved={() => {}} />
      )}

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>Patrol Management</div>
          <div style={S.sub}>Routes · Assign officers · Observations · Quick issues</div>
        </div>
        <button style={S.btn()} onClick={() => { setEditingRoute(null); setShowModal(true); }}>+ New Route</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[['Routes', stats.routes, '#1A1A2E'], ['Completed', stats.completed, '#2E7D52'], ['Incomplete', stats.incomplete, '#C9A84C'], ['Open Issues', stats.issues, '#C0392B']].map(([l, v, c]) => (
          <div key={l} style={{ ...S.card, borderLeft: `3px solid ${c}`, padding: '14px 16px' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c }}>{v}</div>
            <div style={{ fontSize: 10, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, marginTop: 3 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['routes', `Routes (${routes.length})`], ['logs', `Patrol Logs (${patrols.length})`], ['issues', `Quick Issues (${stats.issues} open)`], ['observations', 'Observations']].map(([t, l]) => (
          <button key={t} style={S.tabBtn(tab === t)} onClick={() => setTab(t)}>{l}</button>
        ))}
      </div>

      {/* ROUTES TAB */}
      {tab === 'routes' && (
        routes.length === 0
          ? <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#9B8F7A' }}>No patrol routes yet. Create your first route.</div>
          : <div style={S.grid}>
              {routes.map((route, i) => (
                <div key={i} style={{ ...S.card, borderLeft: `3px solid ${route.active ? '#2E7D52' : '#9B8F7A'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1A1A2E' }}>{route.name}</div>
                      <div style={{ fontSize: 12, color: '#9B8F7A', marginTop: 2 }}>{route.site} · {route.points?.length || 0} checkpoints</div>
                    </div>
                    <span style={S.badge(route.active ? '#2E7D52' : '#9B8F7A')}>{route.active ? 'Active' : 'Inactive'}</span>
                  </div>

                  {/* Checkpoints */}
                  <div style={{ marginBottom: 12 }}>
                    {(route.points || []).slice(0, 4).map((pt, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#C9A84C', flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: '#6B6055' }}>{pt.label}</span>
                      </div>
                    ))}
                    {(route.points || []).length > 4 && <div style={{ fontSize: 11, color: '#9B8F7A', marginTop: 4 }}>+{route.points.length - 4} more checkpoints</div>}
                  </div>

                  {/* Assign officer */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={S.lbl}>Assigned Officer</label>
                    <select style={S.sel} value={route.assigned_to || ''} onChange={e => {
                      const o = officers.find(o => o.uid === e.target.value);
                      if (route._id) assignOfficer(route._id, e.target.value, o?.name || o?.email || '');
                    }}>
                      <option value="">Unassigned</option>
                      {officers.map(o => <option key={o.uid} value={o.uid}>{o.name || o.email}</option>)}
                    </select>
                    {route.assigned_name && <div style={{ fontSize: 11, color: '#2E7D52', marginTop: 4 }}>✓ Assigned to {route.assigned_name}</div>}
                  </div>

                  {/* Schedule */}
                  {route.schedule && (
                    <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 12 }}>
                      Schedule: {route.schedule} {route.schedule_time ? `at ${route.schedule_time}` : ''}
                      {(route.schedule_days || []).length > 0 && ` · ${route.schedule_days.join(', ')}`}
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={{ ...S.btn('#1A1A2E'), flex: 1, padding: '7px 10px', fontSize: 11 }}
                      onClick={() => { setEditingRoute(route); setShowModal(true); }}>Edit</button>
                    <button style={{ ...S.btn(route.active ? '#C9A84C' : '#2E7D52'), padding: '7px 10px', fontSize: 11 }}
                      onClick={() => route._id && updateDoc(doc(db, 'patrol_routes', route._id), { active: !route.active })}>
                      {route.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
      )}

      {/* PATROL LOGS TAB */}
      {tab === 'logs' && (
        <div style={S.card}>
          <div style={{ fontSize: 13, color: '#9B8F7A', marginBottom: 16 }}>Patrol submissions from officers — click to view observations</div>
          {patrols.length === 0
            ? <div style={{ textAlign: 'center', padding: 32, color: '#9B8F7A' }}>No patrol logs yet.</div>
            : patrols.map((patrol, i) => (
              <div key={i}>
                <div onClick={() => setSelectedLog(selectedLog?.id === patrol.id ? null : patrol)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #F4F0E8', cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: patrol.completed ? '#F0FFF4' : '#FFF8E7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                    {patrol.completed ? '✓' : '⚠'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E' }}>{patrol.route_name || 'Unknown route'}</div>
                    <div style={{ fontSize: 11, color: '#9B8F7A' }}>{patrol.site} · {patrol.officer_email} · {fmt(patrol.completed_at || patrol.created_at)}</div>
                    {(patrol.observations || []).length > 0 && (
                      <div style={{ fontSize: 11, color: '#C9A84C', marginTop: 2 }}>📝 {patrol.observations.length} observation{patrol.observations.length > 1 ? 's' : ''} logged</div>
                    )}
                    {(patrol.patrol_points || []).some(p => p.photo_url) && (
                      <div style={{ fontSize: 11, color: '#1E3A5F', marginTop: 2 }}>📷 {(patrol.patrol_points || []).filter(p => p.photo_url).length} checkpoint photo{(patrol.patrol_points || []).filter(p => p.photo_url).length > 1 ? 's' : ''}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: patrol.completed ? '#2E7D52' : '#C9A84C' }}>{patrol.points_checked}/{patrol.points_total}</div>
                    <span style={S.badge(patrol.completed ? '#2E7D52' : '#C9A84C')}>{patrol.completed ? 'Complete' : 'Incomplete'}</span>
                  </div>
                </div>

                {/* Expanded detail — full checkpoint-by-checkpoint log */}
                {selectedLog?.id === patrol.id && (
                  <div style={{ background: '#F8F7F4', borderRadius: 8, padding: 14, margin: '8px 0 12px' }}>
                    {/* Timing summary */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 11, color: '#6B6055' }}>
                      {patrol.started_at && <span>Started {fmt(patrol.started_at)}</span>}
                      {patrol.completed_at && <span>Ended {fmt(patrol.completed_at)}</span>}
                      {patrol.duration_minutes != null && <span>Duration {patrol.duration_minutes} min</span>}
                      <span>{patrol.points_checked}/{patrol.points_total} checkpoints</span>
                    </div>

                    {/* Checkpoint-by-checkpoint (from patrol_points) */}
                    {(patrol.patrol_points || []).length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9B8F7A', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Checkpoints</div>
                        {patrol.patrol_points.map((pt, j) => {
                          const done = pt.checked === true;
                          return (
                            <div key={j} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #E2DDD6', alignItems: 'flex-start' }}>
                              <div style={{ fontSize: 15, flexShrink: 0, color: done ? '#2E7D52' : '#C0392B' }}>{done ? '✓' : '✕'}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1A2E' }}>{pt.label || pt.point_id}</div>
                                <div style={{ fontSize: 10, color: '#9B8F7A', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {done && pt.checked_at && <span>{fmt(pt.checked_at)}</span>}
                                  {done && pt.check_method && <span style={S.badge(pt.check_method === 'nfc' ? '#1E3A5F' : '#9B8F7A')}>{pt.check_method === 'nfc' ? 'NFC' : 'Manual'}</span>}
                                  {!done && <span style={{ color: '#C0392B', fontWeight: 700 }}>Not checked</span>}
                                  {pt.photo_required && !pt.photo_url && <span style={{ color: '#C9A84C', fontWeight: 700 }}>Photo required — missing</span>}
                                </div>
                                {pt.photo_url && <CheckpointPhoto url={pt.photo_url} patrolId={patrol.id} canExport={canExport} />}
                                {(pt.observation || '').trim() && (
                                  <div style={{ marginTop: 6, fontSize: 13, color: '#6B6055', lineHeight: 1.4, background: '#FFFFFF', border: '1px solid #E2DDD6', borderRadius: 8, padding: '8px 10px' }}>
                                    📝 {pt.observation}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* Observations (legacy checkbox flow) */}
                    {(patrol.observations || []).length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9B8F7A', letterSpacing: 1, textTransform: 'uppercase', margin: '12px 0 10px' }}>Observations</div>
                        {patrol.observations.map((obs, j) => (
                          <div key={j} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #E2DDD6' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#C9A84C', marginTop: 5, flexShrink: 0 }} />
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1A2E' }}>{obs.checkpoint}</div>
                              <div style={{ fontSize: 13, color: '#6B6055', marginTop: 2, lineHeight: 1.4 }}>{obs.note}</div>
                              <div style={{ fontSize: 10, color: '#9B8F7A', marginTop: 3 }}>{fmt(obs.timestamp)}</div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {(patrol.patrol_points || []).length === 0 && (patrol.observations || []).length === 0 && (
                      <div style={{ fontSize: 12, color: '#9B8F7A' }}>No checkpoint detail recorded for this patrol.</div>
                    )}
                  </div>
                )}
              </div>
            ))
          }
        </div>
      )}

      {/* QUICK ISSUES TAB */}
      {tab === 'issues' && (
        <div>
          {issues.length === 0
            ? <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#9B8F7A' }}>No quick issues reported yet.</div>
            : issues.map(issue => (
              <div key={issue.id} style={{ ...S.card, borderLeft: `3px solid ${issue.status === 'resolved' ? '#2E7D52' : '#C0392B'}`, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                      <span style={S.badge(issue.status === 'resolved' ? '#2E7D52' : '#C0392B')}>
                        {issue.status === 'resolved' ? 'RESOLVED' : 'OPEN'}
                      </span>
                      <span style={{ fontSize: 11, color: '#9B8F7A' }}>{issue.site} · {issue.officer_name || issue.officer_email}</span>
                      <span style={{ fontSize: 11, color: '#9B8F7A' }}>{fmt(issue.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>{issue.title}</div>
                    <div style={{ fontSize: 13, color: '#6B6055', lineHeight: 1.5 }}>{issue.description}</div>
                    <IssueEvidence issue={issue} canExport={canExport} />
                    {issue.patrol_route && <div style={{ fontSize: 11, color: '#9B8F7A', marginTop: 6 }}>Patrol: {issue.patrol_route} · Checkpoint: {issue.checkpoint}</div>}
                  </div>
                  {issue.status !== 'resolved' && (
                    <button style={{ ...S.btn('#2E7D52'), marginLeft: 12, fontSize: 11 }} onClick={() => resolveIssue(issue.id)}>
                      Resolve
                    </button>
                  )}
                </div>
                {issue.status === 'resolved' && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#2E7D52' }}>✓ Resolved by {issue.resolved_by} at {fmt(issue.resolved_at)}</div>
                )}
              </div>
            ))
          }
        </div>
      )}

      {/* OBSERVATIONS TAB */}
      {tab === 'observations' && (
        <div style={S.card}>
          <div style={{ fontSize: 13, color: '#9B8F7A', marginBottom: 16 }}>All checkpoint observations across all patrols</div>
          {patrols.filter(p => (p.observations || []).length > 0).length === 0
            ? <div style={{ textAlign: 'center', padding: 32, color: '#9B8F7A' }}>No observations logged yet.</div>
            : patrols.filter(p => (p.observations || []).length > 0).map((patrol, i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  {patrol.route_name}
                  <span style={{ fontSize: 11, color: '#9B8F7A', fontWeight: 400 }}>{patrol.officer_email} · {fmt(patrol.completed_at)}</span>
                </div>
                {patrol.observations.map((obs, j) => (
                  <div key={j} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #F4F0E8' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#FFF8E7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>📍</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1A2E' }}>{obs.checkpoint}</div>
                      <div style={{ fontSize: 13, color: '#6B6055', marginTop: 2, lineHeight: 1.4 }}>{obs.note}</div>
                      <div style={{ fontSize: 10, color: '#9B8F7A', marginTop: 3 }}>{fmt(obs.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
